import { useCallback } from 'react';

import { PlateNoteEditor } from '@/lib/crm/record/notes/PlateNoteEditor';
import { cx } from '@/utils/cx';

import type { PropertyInputProps } from './TextAreaInput';

interface MarkdownInputProps extends PropertyInputProps {
  /**
   * Host context. 'form' (default) renders the standalone field chrome —
   * rounded box, ring, shadow — and the full-height typing area. 'card' is
   * for hosts that already provide a floating-card container (a table
   * cell's popped field): chromeless (the host card is the chrome), a
   * compact typing area that grows with content, and an auto-focused caret
   * so entering edit mode drops the user straight into writing. 'inline' is
   * for InlineEditableFieldRow property rows, which float the editor in a
   * CellFieldOverlay over the read cell: chromeless (the overlay's border is
   * the chrome), typing at the property read view's metrics (text-sm/5,
   * matching PropertyValueLabel's property rhythm) with padding that lands the
   * first glyph on the read value's pixel, a natural height that grows with
   * content instead of the 200px form floor, and the caret dropped at the
   * clicked character.
   */
  display?: 'form' | 'card' | 'inline';
  /**
   * Viewport coordinates of the click that opened the editor ('inline'
   * display only). Read and edit text are pixel-matched, so the coords land
   * on the same character in the freshly-mounted editor (see
   * PlateNoteEditor's initialCaretPoint). Null/undefined focuses with the
   * caret at the end instead (pencil-icon or keyboard entry).
   */
  initialCaretPoint?: { x: number; y: number } | null;
}

/**
 * MarkdownInput - Rich text editor for markdown property types.
 *
 * Wraps PlateNoteEditor (the same editor used by MemoForm) so that any
 * property declared with `field_type_name: markdown` gets a full rich
 * text experience: bold, italic, underline, strikethrough, lists,
 * blockquotes, and @mention support.
 *
 * The editor reads and writes HTML.  Stored values render through
 * MarkdownContent (whose always-on raw-HTML pipeline parses and sanitizes
 * it — see PropertyValueLabel.tsx 'markdown' case), so no save-time
 * conversion is required.
 *
 * Note: `propertyType`, `inputRef`, and `textareaRef` from PropertyInputProps
 * are intentionally not destructured.  Plate manages its own focus and
 * selection internally so `inputRef`/`textareaRef` serve no purpose here.
 * `propertyType` carries schema metadata used by other inputs (e.g.
 * TextAreaInput shows a property-type icon) but MarkdownInput has no
 * comparable use for it.  Multi-value markdown is not supported (markdown
 * properties are inherently long-form single values).
 */
export const MarkdownInput: React.FC<MarkdownInputProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder,
  display = 'form',
  initialCaretPoint,
}) => {
  const isCard = display === 'card';
  const isInline = display === 'inline';
  const isForm = display === 'form';

  // Coerce unknown -> string. Plate handles legacy plain text and HTML
  // both via parseContentToNodes (auto-detection).
  const stringValue = typeof value === 'string' ? value : '';

  const handleChange = useCallback(
    (newValue: string) => {
      // Emit undefined when value is cleared so the property is treated
      // as "not set" rather than the empty string.  This mirrors
      // TextAreaInput's behaviour and keeps required-field validation
      // consistent across editors.
      //
      // Plate always renders at least one paragraph, so a cleared editor
      // serializes to `<p></p>` (or `<p><br></p>`), not `''`.  Strip
      // tags and whitespace before deciding emptiness.  Mention tags
      // carry their display name as text content, so a note containing
      // only a mention still strips to non-empty.
      const stripped = newValue.replace(/<[^>]*>/g, '').trim();
      onChange(stripped === '' ? undefined : newValue);
    },
    [onChange]
  );

  // Vertical size: the standalone form keeps the full note-editor height;
  // the table's popped card is compact; the inline row grows with content so
  // the row's height only ever changes when the content does.
  let minHeight: 'default' | 'compact' | 'auto' = 'default';
  if (isCard) {
    minHeight = 'compact';
  } else if (isInline) {
    minHeight = 'auto';
  }

  return (
    <div
      className={cx(
        'w-full',
        // Standalone form chrome; card hosts (a table cell's popped field)
        // already ARE the chrome, so a second border/shadow box would double
        // the borders.
        //
        // A real `border`, not the `ring-1 ring-inset` the single-line inputs
        // use. An inset ring is painted as an inset box-shadow, which any
        // descendant's background paints OVER — and this editor has a
        // full-bleed opaque toolbar as its last child, so the ring visibly
        // washed out alongside the toolbar while staying crisp alongside the
        // text area. A border lives outside the child's box and can't be
        // covered. (Visually identical otherwise: `box-sizing: border-box`
        // keeps the outer dimensions the same.)
        isForm &&
          'rounded-lg bg-primary border border-primary shadow-xs focus-within:border-brand focus-within:ring-1 focus-within:ring-brand',
        isForm &&
          disabled &&
          'cursor-not-allowed bg-disabled_subtle border-disabled',
        // Inline is chromeless like card: its host (InlineEditableFieldRow)
        // floats it in a CellFieldOverlay whose border-2 IS the chrome. The
        // typing area renders at the property READ view's metrics — 14px on
        // 20px lines (`text-sm/5`, the row text size; PropertyValueLabel
        // retunes MarkdownContent's compact paragraphs to leading-5 / mb-1
        // for property values), while the standalone form editor types at
        // text-md. The descendant selector (0,2,0) outranks the element's
        // own text-md (0,1,0). The editor's own paragraph gap is already
        // mb-1, so no margin override is needed for parity.
        isInline && '[&_[contenteditable]]:text-sm/5',
        // Glyph parity inside the floating field: the overlay overhangs the
        // read cell by 3px and draws a 2px border, so its content box starts
        // 1px outside the cell; the read cell pads its value 10px/6px
        // (pl-2.5 py-1.5), so 11px/7px here puts the first glyph on the
        // read value's pixel.
        isInline &&
          '[&_[contenteditable]]:px-[11px] [&_[contenteditable]]:py-[7px]',
        // The floating field is anchored at the row and not shifted up to
        // fit the viewport, so a very long value scrolls INSIDE the typing
        // area rather than hanging the toolbar far below the fold.
        isInline &&
          '[&_[contenteditable]]:max-h-[360px] [&_[contenteditable]]:overflow-y-auto'
      )}
    >
      <PlateNoteEditor
        value={stringValue}
        onChange={handleChange}
        readOnly={disabled}
        placeholder={placeholder ?? 'Write'}
        mentionsEnabled
        richTextEnabled
        minHeight={minHeight}
        // Card: entering edit IS the intent to type. Inline: only when there
        // is no click point (pencil-icon / keyboard entry) — with one, the
        // initialCaretPoint effect owns focus and drops the caret at the
        // clicked character instead of the end.
        autoFocus={(isCard || (isInline && !initialCaretPoint)) && !disabled}
        initialCaretPoint={isInline ? initialCaretPoint : undefined}
      />
    </div>
  );
};
